import React, { useState, useEffect, useRef } from "react";

function SearchableSelect({ 
  value, 
  onChange, 
  options, 
  placeholder = "Select an option",
  label,
  displayKey = "name",
  valueKey = "id",
  style = {}
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = options.filter(option => {
    const displayValue = typeof option === 'string' ? option : option[displayKey];
    // Handle null, undefined, or non-string display values
    if (displayValue === null || displayValue === undefined) return false;
    return String(displayValue).toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Get display value
  const getDisplayValue = () => {
    if (!value) return "";
    const option = options.find(opt => {
      const optValue = typeof opt === 'string' ? opt : opt[valueKey];
      return optValue == value;
    });
    if (!option) return "";
    return typeof option === 'string' ? option : option[displayKey];
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        if (!value) setSearchTerm("");
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // If dropdown is closed, open it on Arrow Down or Enter
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        setIsOpen(true);
        e.preventDefault();
        e.stopPropagation();
      }
      return;
    }

    // If dropdown is open, handle navigation
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        e.stopPropagation();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        e.stopPropagation();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        e.stopPropagation();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          const selected = filteredOptions[highlightedIndex];
          const selectedValue = typeof selected === 'string' ? selected : selected[valueKey];
          onChange(selectedValue);
          setIsOpen(false);
          setSearchTerm("");
          setHighlightedIndex(-1);
        }
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(false);
        if (!value) setSearchTerm("");
        setHighlightedIndex(-1);
        break;
      case 'Tab':
        setIsOpen(false);
        if (!value) setSearchTerm("");
        setHighlightedIndex(-1);
        break;
      case 'Backspace':
      case 'Delete':
        if (searchTerm === "" && value) {
          onChange("");
        }
        break;
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);
    
    // If user clears the input, clear the value too
    if (newValue === "") {
      onChange("");
    }
  };

  // Handle selection
  const handleSelect = (option) => {
    const selectedValue = typeof option === 'string' ? option : option[valueKey];
    onChange(selectedValue);
    setIsOpen(false);
    setSearchTerm("");
    setHighlightedIndex(-1);
  };

  // Highlight matching text
  const highlightText = (text, search) => {
    if (!search) return text;
    const parts = text.split(new RegExp(`(${search})`, 'gi'));
    return parts.map((part, i) => 
      part.toLowerCase() === search.toLowerCase() 
        ? <span key={i} style={{ backgroundColor: '#fef08a', fontWeight: 600 }}>{part}</span>
        : part
    );
  };

  // Handle focus
  const handleFocus = () => {
    setIsOpen(true);
    setSearchTerm("");
  };

  // Handle blur
  const handleBlur = () => {
    // Delay closing to allow click on option
    setTimeout(() => {
      if (!value && searchTerm) {
        // If there's no value but there's search term, try to find matching option
        const match = options.find(opt => {
          const displayValue = typeof opt === 'string' ? opt : opt[displayKey];
          return displayValue.toLowerCase() === searchTerm.toLowerCase();
        });
        if (match) {
          const matchValue = typeof match === 'string' ? match : match[valueKey];
          onChange(matchValue);
        }
      }
      if (!value) {
        setSearchTerm("");
      }
    }, 200);
  };

  const styles = {
    container: {
      position: 'relative',
      marginBottom: '14px',
      ...style
    },
    label: {
      display: 'block',
      fontSize: '12px',
      fontWeight: 600,
      color: '#374151',
      marginBottom: '6px'
    },
    selectWrapper: {
      position: 'relative'
    },
    input: {
      width: '100%',
      height: '38px',
      padding: '6px 35px 6px 12px',
      fontSize: '13px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      background: '#fff',
      cursor: 'pointer',
      boxSizing: 'border-box',
      outline: 'none',
      fontFamily: 'inherit'
    },
    inputFocused: {
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.1)'
    },
    dropdown: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      background: '#fff',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      maxHeight: '250px',
      overflowY: 'auto',
      zIndex: 1000,
      marginTop: '4px'
    },
    option: {
      padding: '10px 12px',
      fontSize: '13px',
      cursor: 'pointer',
      borderBottom: '1px solid #f3f4f6'
    },
    optionHighlighted: {
      background: '#f3f4f6'
    },
    optionSelected: {
      background: '#e0e7ff'
    },
    arrow: {
      position: 'absolute',
      right: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      fontSize: '10px',
      color: '#6b7280',
      pointerEvents: 'none'
    }
  };

  return (
    <div ref={wrapperRef} style={styles.container}>
      {label && <label style={styles.label}>{label}</label>}
      <div style={styles.selectWrapper}>
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : (value ? getDisplayValue() : "")}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          autoComplete="off"
          style={{
            ...styles.input,
            ...(isOpen ? styles.inputFocused : {}),
            ...(value && !isOpen ? { color: '#1f2937' } : {}),
            ...(!value && !isOpen ? { color: '#9ca3af' } : {})
          }}
        />
        <span style={styles.arrow}>▼</span>
      </div>
      
      {isOpen && (
        <div style={styles.dropdown}>
          {filteredOptions.length === 0 ? (
            <div style={{ padding: '12px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
              No options found
            </div>
          ) : (
            filteredOptions.map((option, index) => {
              const displayValue = typeof option === 'string' ? option : option[displayKey];
              const optionValue = typeof option === 'string' ? option : option[valueKey];
              const isSelected = optionValue == value;
              
              return (
                <div
                  key={optionValue}
                  style={{
                    ...styles.option,
                    ...(index === highlightedIndex ? styles.optionHighlighted : {}),
                    ...(isSelected ? styles.optionSelected : {})
                  }}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {highlightText(displayValue, searchTerm)}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export default SearchableSelect;
